import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const AboutPage = () => {
  const values = [
    'Objectivity in analysis and recommendations',
    'Transparency in methodology and findings',
    'Commitment to client outcomes over billable hours',
    'Continuous learning in a changing energy landscape'
  ];

  const clientTypes = [
    'Commercial Real Estate',
    'Manufacturing Facilities',
    'Municipal Governments',
    'Healthcare Systems',
    'Educational Institutions',
    'Data Centers'
  ];

  const awards = [
    { image: '/award-diamond.png', title: 'Diamond Award Winner', subtitle: '2026' },
    { image: '/award-customer-service.png', title: 'Top 10 Customer Service', subtitle: 'Awarded by Front Runner' },
    { image: '/award-five-star.png', title: 'Five Star Award Energy', subtitle: 'Brick, Inc - Jan 2026' }
  ];

  return (
    <main className="page-content" data-testid="about-page">
      {/* Page Header */}
      <section className="page-header-with-image">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">About Us</span>
          <h1 className="page-title" style={{marginTop: '0.5rem'}}>About Last Mile Advisors</h1>
          <p className="page-subtitle">
            Independent energy and utility cost advisory for organizations that want clarity and results.
          </p>
        </div>
      </section>

      {/* Awards Banner */}
      <section className="section-dark" style={{padding: '2rem 0', borderBottom: '1px solid var(--border-default)'}}>
        <div className="container">
          <div className="awards-banner" data-testid="about-awards-banner">
            {awards.map((award, index) => (
              <div key={index} className="award-badge">
                <img src={award.image} alt={award.title} />
                <div className="award-badge-text">
                  <span>{award.title}</span>
                  <span>{award.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section section-light">
        <div className="container">
          <div className="about-mission-grid">
            <div className="about-mission-content">
              <span className="overline">Our Mission</span>
              <h2 className="about-heading" style={{marginTop: '0.5rem'}}>Our Mission</h2>
              <p className="about-text-large">
                To provide organizations with the clarity and expertise needed to make informed decisions about their energy and utility costs.
              </p>
              <Separator className="my-8" />
              <span className="overline">Philosophy</span>
              <h2 className="about-heading" style={{marginTop: '0.5rem'}}>Our Philosophy</h2>
              <p className="about-text">
                We believe that effective energy management starts with understanding. Our role is to translate complex utility structures, regulatory requirements, and market conditions into clear, actionable insights.
              </p>
            </div>
            <div className="about-mission-image">
              <img
                src="https://images.unsplash.com/photo-1694702730868-a34da58c1427?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc1MjUwMDk2fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern glass office building"
                className="mission-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="about-approach-grid">
            <div className="about-approach-image">
              <img
                src="https://images.unsplash.com/photo-1714330859170-695ea7edd2ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMHN1bnNldCUyMGVuZXJneXxlbnwwfHx8fDE3NzUyNTAwOTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Solar panels at sunset"
                className="approach-image"
              />
            </div>
            <div className="about-approach-content">
              <span className="overline">How We Work</span>
              <h2 className="about-heading" style={{marginTop: '0.5rem'}}>Our Approach</h2>
              <p className="about-text">
                Every engagement begins with listening. We work to understand your organization's unique situation before developing recommendations. Our analysis is thorough, our advice is practical, and our focus is always on delivering value.
              </p>
              <div className="approach-features">
                <div className="approach-feature">
                  <CheckCircle size={18} className="approach-check" />
                  <span>Comprehensive utility bill analysis</span>
                </div>
                <div className="approach-feature">
                  <CheckCircle size={18} className="approach-check" />
                  <span>Market-based rate comparisons</span>
                </div>
                <div className="approach-feature">
                  <CheckCircle size={18} className="approach-check" />
                  <span>Regulatory compliance review</span>
                </div>
                <div className="approach-feature">
                  <CheckCircle size={18} className="approach-check" />
                  <span>Implementation support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Full Section */}
      <section className="awards-section" data-testid="about-awards-full">
        <div className="container">
          <div className="awards-header">
            <span className="overline">Recognition</span>
            <h2>Industry Awards & Recognition</h2>
          </div>
          <div className="awards-grid">
            {awards.map((award, index) => (
              <div key={index} className="award-card">
                <img src={award.image} alt={award.title} className="award-image" />
                <div className="award-title">{award.title}</div>
                <div className="award-subtitle">{award.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <span className="overline">Values</span>
            <h2 className="section-title-light">What We Stand For</h2>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-item">
                <div className="value-number">{index + 1}</div>
                <p className="value-text">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Focus */}
      <section className="section section-dark">
        <div className="container">
          <div className="about-content">
            <span className="overline">Independence</span>
            <h2 className="about-heading" style={{marginTop: '0.5rem'}}>Client-First Positioning</h2>
            <p className="about-text">
              We are not affiliated with any energy supplier, utility company, or equipment vendor. This independence is fundamental to our ability to provide objective advice. Our only interest is in helping you achieve your goals.
            </p>
            <p className="about-text mt-6">
              When we recommend a strategy, it's because our analysis shows it will benefit you -- not because we have a financial interest in the outcome.
            </p>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <span className="overline">Industries</span>
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle">
              We work with organizations across sectors that have significant energy and utility expenses.
            </p>
          </div>
          <div className="industries-grid">
            {clientTypes.map((client, index) => (
              <div key={index} className="industry-item">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta-hero">
        <div className="section-cta-overlay"></div>
        <div className="container section-cta-content">
          <div className="cta-content">
            <h2 className="cta-title">Let's Work Together</h2>
            <p className="cta-subtitle">
              Contact us to discuss how we can help your organization take control of its energy costs.
            </p>
            <Link to="/contact">
              <Button className="btn-primary btn-lg" data-testid="about-cta-btn">
                Request a Consultation
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
